import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Gift, Award, TrendingUp, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LoyaltyProgram() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-gold/20">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full mb-6">
              <Award className="w-12 h-12 text-black" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Programme <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-yellow-500">Fidélité</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Plus vous voyagez avec nous, plus vous économisez !
            </p>
          </div>

          {/* How it Works */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Star className="w-6 h-6 text-amber-500" />
              Comment ça marche ?
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-500 font-bold text-xl">
                  1
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Créez votre compte</h3>
                  <p className="text-gray-300">
                    Inscrivez-vous gratuitement en quelques secondes. Aucune carte de crédit requise.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-500 font-bold text-xl">
                  2
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Réservez vos trajets</h3>
                  <p className="text-gray-300">
                    Chaque trajet complété compte pour votre programme de fidélité.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-500 font-bold text-xl">
                  3
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Économisez !</h3>
                  <p className="text-gray-300">
                    Après 5 courses complétées, bénéficiez automatiquement de <strong className="text-amber-500">10% de réduction</strong> sur votre prochaine réservation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
              <Gift className="w-10 h-10 text-amber-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Réductions Automatiques</h3>
              <p className="text-gray-300">
                Pas besoin de codes promo ! Votre réduction s'applique automatiquement à chaque 6ème course.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
              <TrendingUp className="w-10 h-10 text-amber-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Progression Claire</h3>
              <p className="text-gray-300">
                Suivez votre progression en temps réel dans votre espace client.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
              <CheckCircle className="w-10 h-10 text-amber-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Sans Expiration</h3>
              <p className="text-gray-300">
                Vos points fidélité n'expirent jamais. Prenez votre temps !
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
              <Award className="w-10 h-10 text-amber-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Statut VIP</h3>
              <p className="text-gray-300">
                Les clients réguliers peuvent obtenir le statut VIP avec des avantages exclusifs.
              </p>
            </div>
          </div>

          {/* Example */}
          <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-2 border-amber-500/50 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">💡 Exemple Concret</h2>
            <div className="space-y-3 text-gray-200">
              <p className="flex items-center gap-3">
                <span className="text-amber-500 font-bold">Course 1-5 :</span>
                Prix normal (ex: 50€ chacune)
              </p>
              <p className="flex items-center gap-3">
                <span className="text-green-500 font-bold text-lg">Course 6 :</span>
                50€ - 10% = <strong className="text-green-400 text-xl">45€ 🎉</strong>
              </p>
              <p className="flex items-center gap-3 text-lg font-semibold">
                <span className="text-amber-500">Total économisé :</span>
                <span className="text-green-400">5€ par cycle</span>
              </p>
            </div>
            <p className="text-center text-gray-400 text-sm mt-6">
              Plus vous voyagez, plus vous économisez !
            </p>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              onClick={() => navigate('/client/signup')}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold text-lg px-12 py-6 shadow-lg shadow-amber-500/30"
            >
              Rejoindre le Programme Fidélité
            </Button>
            <p className="text-gray-400 text-sm mt-4">
              Déjà membre ?{' '}
              <button
                onClick={() => navigate('/client/login')}
                className="text-amber-500 hover:text-amber-400 font-medium"
              >
                Connectez-vous
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
